import type { RouteInfo } from "../types/routes.js";
import type { ToolDefinition, ToolExecutor, ToolManifest } from "../types/tools.js";

export type RouteSource = {
  id?: string;
  getRoutes: () => Promise<RouteInfo[]>;
};

type RouteSourceFn = () => Promise<RouteInfo[]>;

type RouteSourceInputItem = RouteSource | RouteSourceFn | RouteInfo[];

export type RouteSourceInput = RouteSourceInputItem | RouteSourceInputItem[];

export type ToolSource = {
  id?: string;
  getTools: () => Promise<ToolDefinition[]>;
  executeTool?: ToolExecutor;
};

type ToolSourceFn = () => Promise<ToolDefinition[] | ToolManifest>;

type ToolSourceInputItem =
  | ToolSource
  | ToolSourceFn
  | ToolDefinition[]
  | ToolManifest
  | {
      getTools: ToolSourceFn;
      executeTool?: ToolExecutor;
    };

export type ToolSourceInput = ToolSourceInputItem | ToolSourceInputItem[];

function isRouteSource(value: unknown): value is RouteSource {
  return Boolean(
    value &&
      typeof value === "object" &&
      "getRoutes" in value &&
      typeof (value as RouteSource).getRoutes === "function"
  );
}

function isRouteInfoArray(value: unknown): value is RouteInfo[] {
  return Array.isArray(value) && value.every((route) => route && typeof route.path === "string");
}

function normalizeRouteSourceItem(item: RouteSourceInputItem, index: number): RouteSource {
  if (isRouteSource(item)) {
    return {
      id: item.id ?? `route-source-${index}`,
      getRoutes: item.getRoutes,
    };
  }

  if (typeof item === "function") {
    return {
      id: `route-fn-${index}`,
      getRoutes: item,
    };
  }

  if (isRouteInfoArray(item)) {
    return {
      id: `route-static-${index}`,
      getRoutes: async () => item,
    };
  }

  throw new Error("Unsupported route source input");
}

export function normalizeRouteSources(input: RouteSourceInput): RouteSource[] {
  if (Array.isArray(input)) {
    if (input.length === 0) {
      return [];
    }
    if (isRouteInfoArray(input)) {
      return [normalizeRouteSourceItem(input, 0)];
    }
    return (input as RouteSourceInputItem[]).map((item, index) =>
      normalizeRouteSourceItem(item, index)
    );
  }

  return [normalizeRouteSourceItem(input, 0)];
}

function isToolSource(value: unknown): value is ToolSource {
  return Boolean(
    value &&
      typeof value === "object" &&
      "getTools" in value &&
      typeof (value as ToolSource).getTools === "function"
  );
}

function isToolManifest(value: unknown): value is ToolManifest {
  return Boolean(value && typeof value === "object" && "tools" in value);
}

function isToolDefinitionArray(value: unknown): value is ToolDefinition[] {
  return Array.isArray(value) && value.every((tool) => tool && typeof tool.name === "string");
}

function toToolDefinitions(result: unknown): ToolDefinition[] {
  if (isToolDefinitionArray(result)) {
    return result;
  }
  if (isToolManifest(result)) {
    return result.tools;
  }
  throw new Error("Tool source must resolve to ToolDefinition[] or ToolManifest");
}

function normalizeToolSourceItem(item: ToolSourceInputItem, index: number): ToolSource {
  if (isToolSource(item)) {
    return {
      id: item.id ?? `tool-source-${index}`,
      getTools: async () => {
        const result = await item.getTools();
        return toToolDefinitions(result);
      },
      executeTool: item.executeTool,
    };
  }

  if (typeof item === "function") {
    return {
      id: `tool-fn-${index}`,
      getTools: async () => {
        const result = await item();
        return toToolDefinitions(result);
      },
    };
  }

  if (isToolDefinitionArray(item)) {
    return {
      id: `tool-static-${index}`,
      getTools: async () => item,
    };
  }

  if (isToolManifest(item)) {
    return {
      id: `tool-manifest-${index}`,
      getTools: async () => item.tools,
    };
  }

  if (
    item &&
    typeof item === "object" &&
    "getTools" in item &&
    typeof item.getTools === "function"
  ) {
    return {
      id: (item as { id?: string }).id ?? `tool-wrapper-${index}`,
      getTools: async () => {
        const result = await item.getTools();
        return toToolDefinitions(result);
      },
      executeTool: item.executeTool,
    };
  }

  throw new Error("Unsupported tool source input");
}

export function normalizeToolSources(input?: ToolSourceInput): ToolSource[] {
  if (!input) {
    return [];
  }
  if (Array.isArray(input)) {
    if (input.length === 0) {
      return [];
    }
    if (isToolDefinitionArray(input)) {
      return [normalizeToolSourceItem(input, 0)];
    }
    return (input as ToolSourceInputItem[]).map((item, index) =>
      normalizeToolSourceItem(item, index)
    );
  }

  return [normalizeToolSourceItem(input, 0)];
}
